import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Save, Plus, Trash2, CreditCard, CheckCircle2, XCircle,
  Clock, ExternalLink, Crown,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ImageUpload, { fileUrl } from "@/components/ImageUpload";
import { fmtDate } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const PERIODS = [
  { v: "month", l: "Monthly" },
  { v: "year", l: "Yearly" },
  { v: "one_time", l: "One-time" },
];
const TIERS = [
  { v: "club_pro", l: "Club Pro" },
  { v: "umpire", l: "Umpire" },
  { v: "free", l: "Free" },
];
const STATUS_META = {
  pending: { l: "Pending", color: "#eab308", Icon: Clock },
  approved: { l: "Approved", color: "#22c55e", Icon: CheckCircle2 },
  rejected: { l: "Rejected", color: "#ef4444", Icon: XCircle },
};

const fieldClass = "w-full rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400";

function slug() { return `plan_${Math.random().toString(36).slice(2, 8)}`; }

export default function AdminSubscriptions({ onPendingChange }) {
  const [cfg, setCfg] = useState(null);
  const [tierOpts, setTierOpts] = useState(TIERS);
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [proofOpen, setProofOpen] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [acting, setActing] = useState(false);

  const loadRequests = useCallback(() => {
    api.get(`/admin/subscriptions${filter !== "all" ? `?status=${filter}` : ""}`)
      .then(({ data }) => setRequests(data || []))
      .catch(() => setRequests(false));
  }, [filter]);

  useEffect(() => {
    api.get("/config/subscription").then(({ data }) => setCfg(data)).catch(() => setCfg(false));
    api.get("/config/tiers").then(({ data }) => setTierOpts((data.tiers || []).map((t) => ({ v: t.id, l: t.name })))).catch(() => {});
  }, []);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const sym = cfg?.currency_symbol || "\u20b1";

  const setProduct = (i, patch) =>
    setCfg((c) => ({ ...c, products: c.products.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) }));
  const addProduct = () =>
    setCfg((c) => ({ ...c, products: [...(c.products || []), { id: slug(), name: "New Plan", price: 0, period: "month", tier: "club_pro", duration_days: 30 }] }));
  const removeProduct = (i) => setCfg((c) => ({ ...c, products: c.products.filter((_, idx) => idx !== i) }));

  const saveConfig = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/subscription", {
        enabled: !!cfg.enabled,
        payment_qr_url: cfg.payment_qr_url || null,
        payment_details: cfg.payment_details || "",
        products: (cfg.products || []).map((p) => ({
          id: p.id, name: p.name, price: Number(p.price) || 0,
          period: p.period, tier: p.tier, duration_days: Number(p.duration_days) || 30,
        })),
      });
      setCfg(data);
      toast.success("Subscription settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const approve = async (r) => {
    setActing(true);
    try {
      await api.post(`/admin/subscriptions/${r.request_id}/approve`);
      toast.success(`${r.user_name || r.user_email} upgraded`);
      loadRequests();
      onPendingChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    } finally {
      setActing(false);
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    setActing(true);
    try {
      await api.post(`/admin/subscriptions/${rejecting.request_id}/reject`, { note: rejectNote });
      toast.success("Request rejected");
      setRejecting(null);
      setRejectNote("");
      loadRequests();
      onPendingChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reject failed");
    } finally {
      setActing(false);
    }
  };

  if (cfg === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (cfg === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load subscription settings.</p>;

  return (
    <div className="space-y-8" data-testid="admin-subscriptions">
      {/* Requests inbox */}
      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Crown className="w-5 h-5 text-primary-c" /> Purchase requests</h3>
          <div className="glass rounded-full p-0.5 flex gap-0.5" data-testid="sub-filter">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button key={f} data-testid={`sub-filter-${f}`} onClick={() => setFilter(f)}
                className={`rounded-full px-3 py-1.5 text-xs font-bold capitalize transition-colors ${filter === f ? "text-[var(--bg)]" : ""}`}
                style={filter === f ? { background: "var(--primary)" } : { color: "var(--text-2)" }}>{f}</button>
            ))}
          </div>
        </div>

        {requests === null && <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>}
        {requests === false && <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load requests.</p>}
        {Array.isArray(requests) && requests.length === 0 && (
          <p className="rounded-2xl glow-border p-6 text-sm text-center" style={{ background: "var(--surface)", color: "var(--text-2)" }} data-testid="sub-requests-empty">
            No {filter !== "all" ? filter : ""} requests.
          </p>
        )}
        {Array.isArray(requests) && requests.length > 0 && (
          <div className="grid gap-3" data-testid="sub-requests-list">
            {requests.map((r) => {
              const m = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <div key={r.request_id} data-testid={`sub-request-${r.request_id}`}
                  className="rounded-2xl glow-border p-4 flex flex-wrap items-center gap-4 justify-between" style={{ background: "var(--surface)" }}>
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2">
                      {r.user_name || "—"}
                      <span className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 flex items-center gap-1" style={{ color: m.color }}>
                        <m.Icon className="w-3 h-3" /> {m.l}
                      </span>
                    </div>
                    <div className="text-xs mono truncate" style={{ color: "var(--text-2)" }}>{r.user_email}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>
                      {r.product_name} · {sym}{Number(r.price).toLocaleString()} · {fmtDate(r.created_at)}
                    </div>
                    {r.user_note && <div className="text-xs mt-1 italic" style={{ color: "var(--text-2)" }}>“{r.user_note}”</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`sub-view-proof-${r.request_id}`} onClick={() => setProofOpen(r)}
                      className="glass rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}>
                      <ExternalLink className="w-3.5 h-3.5 text-primary-c" /> Proof
                    </button>
                    {r.status === "pending" && (
                      <>
                        <button data-testid={`sub-approve-${r.request_id}`} onClick={() => approve(r)} disabled={acting}
                          className="btn-glow rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button data-testid={`sub-reject-${r.request_id}`} onClick={() => { setRejecting(r); setRejectNote(""); }} disabled={acting}
                          className="glass rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "#ef4444" }}>
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment settings */}
      <div className="border-t pt-8" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary-c" /> Payment settings</h3>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="sub-enabled-toggle">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))} />
            Online subscriptions enabled
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
          <ImageUpload value={cfg.payment_qr_url} onChange={(u) => setCfg((c) => ({ ...c, payment_qr_url: u }))}
            testid="sub-qr-upload" label="Payment QR image" aspect="aspect-square" hint="GCash / bank QR (public)" />
          <div>
            <label className="text-xs uppercase tracking-wider mono" style={{ color: "var(--text-2)" }}>Payment details / instructions</label>
            <textarea data-testid="sub-payment-details" rows={6} value={cfg.payment_details || ""}
              onChange={(e) => setCfg((c) => ({ ...c, payment_details: e.target.value }))}
              placeholder="e.g. GCash: 0917 000 0000 (Juan D.) — send the exact amount, then upload your receipt."
              className={`${fieldClass} mt-2`} style={{ color: "var(--text)" }} />
          </div>
        </div>

        {/* Products / tiers editor */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs uppercase tracking-wider mono" style={{ color: "var(--text-2)" }}>Plans & pricing</label>
            <button data-testid="sub-add-product" onClick={addProduct}
              className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}>
              <Plus className="w-3.5 h-3.5 text-primary-c" /> Add plan
            </button>
          </div>
          <div className="grid gap-3">
            {(cfg.products || []).map((p, i) => (
              <div key={i} data-testid={`sub-product-${i}`} className="rounded-2xl glow-border p-4 grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr_1fr_1fr_auto] gap-3 items-end" style={{ background: "var(--surface)" }}>
                <div>
                  <span className="text-[10px] uppercase mono" style={{ color: "var(--text-2)" }}>Name</span>
                  <input data-testid={`sub-product-name-${i}`} value={p.name} onChange={(e) => setProduct(i, { name: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
                </div>
                <div>
                  <span className="text-[10px] uppercase mono" style={{ color: "var(--text-2)" }}>Price ({sym})</span>
                  <input data-testid={`sub-product-price-${i}`} type="number" min="0" value={p.price} onChange={(e) => setProduct(i, { price: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
                </div>
                <div>
                  <span className="text-[10px] uppercase mono" style={{ color: "var(--text-2)" }}>Period</span>
                  <select data-testid={`sub-product-period-${i}`} value={p.period} onChange={(e) => setProduct(i, { period: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }}>
                    {PERIODS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
                <div>
                  <span className="text-[10px] uppercase mono" style={{ color: "var(--text-2)" }}>Tier</span>
                  <select data-testid={`sub-product-tier-${i}`} value={p.tier} onChange={(e) => setProduct(i, { tier: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }}>
                    {tierOpts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
                <div>
                  <span className="text-[10px] uppercase mono" style={{ color: "var(--text-2)" }}>Days valid</span>
                  <input data-testid={`sub-product-days-${i}`} type="number" min="1" value={p.duration_days} onChange={(e) => setProduct(i, { duration_days: e.target.value })} className={fieldClass} style={{ color: "var(--text)" }} />
                </div>
                <button data-testid={`sub-product-remove-${i}`} onClick={() => removeProduct(i)} aria-label="Remove plan"
                  className="glass rounded-lg w-9 h-9 flex items-center justify-center hover:scale-105 transition-transform">
                  <Trash2 className="w-4 h-4" style={{ color: "#ef4444" }} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <button data-testid="sub-save-config" onClick={saveConfig} disabled={busy}
          className="btn-glow rounded-full px-6 py-3 mt-6 font-bold inline-flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </button>
      </div>

      {/* Proof dialog */}
      <Dialog open={!!proofOpen} onOpenChange={(o) => !o && setProofOpen(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="sub-proof-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Payment proof</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              {proofOpen?.user_name} — {proofOpen?.product_name}
            </DialogDescription>
          </DialogHeader>
          {proofOpen?.proof_url ? (
            <img src={fileUrl(proofOpen.proof_url)} alt="Payment proof" className="w-full rounded-xl glow-border" data-testid="sub-proof-image" />
          ) : (
            <p className="text-sm" style={{ color: "var(--text-2)" }}>No proof attached.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="sub-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><XCircle className="w-5 h-5" style={{ color: "#ef4444" }} /> Reject request</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              Optionally tell {rejecting?.user_name || "the user"} why — they'll see this message.
            </DialogDescription>
          </DialogHeader>
          <textarea data-testid="sub-reject-note" rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            placeholder="e.g. Payment amount doesn't match, or screenshot unreadable."
            className={fieldClass} style={{ color: "var(--text)" }} />
          <DialogFooter>
            <button data-testid="sub-reject-confirm" onClick={doReject} disabled={acting}
              className="rounded-lg px-4 py-2 text-sm font-bold bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 disabled:opacity-50">
              {acting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Reject request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
